import { ReactNode } from 'react';

import { WaitlistEntryCreateAppointmentCommand } from '@dreryk/egabinet-waitlist-frontend-api';
import { AppointmentCreationResult, AppointmentTimeSlotOccupiedFault } from '@dreryk/egabinet-appointment-frontend-api';
import { ReceptionServiceId } from '@dreryk/egabinet-private-medical-service-published-language-api';
import { TimeSlot } from '@dreryk/time-slot-common-api';
import { ServiceLocationId, ServiceLocationType } from '@dreryk/egabinet-healthcare-provider-published-language-api';

import { useCqrsCommand } from '@dreryk/command-query-react-common';
import { ActionEventInterceptor, dispatchSnackbarNegativeEvent } from '@dreryk/react-components';
import { Choice } from '@dreryk/watermelon';

import { AppointmentAdvancePaymentShowModalEventInterceptor } from '~libs.desktop.shared.appointment-advance-payment';

import { appointmentsTranslationKeys } from '../../../../translations';
import { parseToDateWithMidnightAdjustment } from '../../shared/form/AppointmentForm.utils';
import {
   AppointmentWaitlistCreateFormBean,
   AppointmentWaitlistCreateFormController,
} from '../waitlist/AppointmentWaitlistCreateForm.types';

import { getFirstAppointmentId } from './AppointmentCreateActionEventInterceptor.utils';

type AppointmentWaitlistCreateActionEventInterceptorProps = Readonly<
   Pick<AppointmentWaitlistCreateFormController, 'handleSubmit'> & {
      children: ReactNode;
      onAppointmentTimeSlotOccupiedFault: VoidFunction;
      waitlistEntryId: number;
      onSuccess: (dateConfirmedWithPatient: boolean) => void;
   }
>;

export const AppointmentWaitlistCreateActionEventInterceptor = (
   props: AppointmentWaitlistCreateActionEventInterceptorProps
): JSX.Element => {
   const { executeAsync: createAppointment, data: appointmentCreationResult } = useCqrsCommand<
      AppointmentCreationResult,
      AppointmentWaitlistCreateFormBean,
      AppointmentTimeSlotOccupiedFault
   >(
      (parameters) => {
         const command = new WaitlistEntryCreateAppointmentCommand();
         command.locationId = ServiceLocationId.of(
            parameters.healthcareProviderLocation!.locationIdentifier.type as ServiceLocationType,
            parameters.healthcareProviderLocation!.locationIdentifier.locationId
         );
         command.timeSlot = TimeSlot.of(
            parseToDateWithMidnightAdjustment('START', parameters.date, parameters.startTime),
            parseToDateWithMidnightAdjustment('END', parameters.date, parameters.endTime)
         );
         command.practitionerId = parameters.practitioner.id;
         command.note = Choice.withUseDefaultIfNull(parameters.note);
         command.encounterMode = parameters.videoConsultation ? 'VIDEO_CONSULTATION' : 'ON_SITE';
         command.waitlistEntryId = props.waitlistEntryId;
         command.timeSlotConfirmed = parameters.dateConfirmedWithPatient;

         if (parameters.receptionService?.value) {
            command.receptionServiceIds = parameters.receptionService.value.map(
               ({ receptionService: { receptionServiceId } }) => ReceptionServiceId.of(receptionServiceId)
            );
         }
         return command;
      },
      {
         onSuccess: (_result, parameters) => {
            props.onSuccess(parameters.dateConfirmedWithPatient);
         },
         onFault: {
            'egabinet-appointment-frontend-api:AppointmentTimeSlotOccupiedFault': () => {
               dispatchSnackbarNegativeEvent(appointmentsTranslationKeys.timeSlotTaken);
               props.onAppointmentTimeSlotOccupiedFault();
            },
         },
      }
   );

   return (
      <ActionEventInterceptor action={props.handleSubmit(createAppointment)}>
         <AppointmentAdvancePaymentShowModalEventInterceptor
            appointmentId={getFirstAppointmentId(appointmentCreationResult)}
         >
            {props.children}
         </AppointmentAdvancePaymentShowModalEventInterceptor>
      </ActionEventInterceptor>
   );
};
